//import { Money } from 'ts-money';
import {
  OBJECT_TYPE_DICTIONARY,
  //TABLE_LOCATIONS,
  PERSONNEL_OBJECT_TYPE_ID,
  NONPERSONNEL_OBJECT_TYPE_ID,
  TABLE_COLUMN_WIDTH,
} from '../../../static/constants.js';
import { COST_ALLOCATION_TOGGLE_BUTTON_LABELS } from '../costAllocationHelpers.js';
import { getMoneyObj } from '../../reusable/MoneyJsHelpers.js';
import { DESCRIPTION, PERSONNEL, PROGRAM, PROGRAM_DEPARTMENT, PROGRAM_DIVISION, USER_GROUP } from '../../../static/keyWords.js';
import { totalCostFormat } from '../../reusable/reusableFunctions.js';
import { ToastComponent } from '@tylertech/forge';
import { messageManager } from '../../System/messageManager/messageManager.js';
import { AGGridColumnNumberComparator } from '../../System/AgGrid/RegisterAgGridModules.js';

const LIGHT_THEME_COLOR = '#000000';
const DARK_THEME_COLOR = '#E0E3FB';
const LIGHT = 'light';
const QUARTILE_COLOR = '#765DAC';

const BUTTONS = Object.freeze({
  EVENLY: 'evenly',
  FTE: 'fte',
});

const allocateProportionally = (
  selectedPrograms,
  byButton,
  combinedTotalCostOfItems,
  selectedItemsAllocations,
  nameConventions
) => {
  const _selectedItemAllocations = structuredClone(selectedItemsAllocations);
  let proportionArray = [];
  let dollarsArray = [];

  proportionArray = buildProportionArray(
    byButton,
    selectedPrograms,
    proportionArray
  );
  const isFteZero = totalFteIsZeroGuard(proportionArray);
  if (isFteZero) {
    ToastComponent.present({
      message: messageManager(nameConventions).COST_ALLOCATION.FTE_TOTAL_ZERO_ERROR,
    });
    return _selectedItemAllocations;
  }

  let notSelectedProgramDollars = calculateNotSelectedProgramDollars(
    _selectedItemAllocations,
    selectedPrograms
  );
  dollarsArray = allocateAvailableDollars(
    notSelectedProgramDollars,
    combinedTotalCostOfItems,
    proportionArray
  );

  assignSelectedProgramsAndSelectedItemAllocations(
    selectedPrograms,
    dollarsArray,
    _selectedItemAllocations
  );

  let updatedDollarsArray = _selectedItemAllocations.map(
    (alloc) => Math.abs(alloc.dollars * 100)
  );

  let updatedPercentArray = getMoneyObj(100).allocate(updatedDollarsArray);

  _selectedItemAllocations.forEach((allocation, i) => {
    allocation.percent = updatedPercentArray[i].amount / 100;
  });

  return _selectedItemAllocations;
};

const buildProportionArray = (byButton, selectedPrograms, proportionArray) => {
  if (byButton === BUTTONS.EVENLY) {
    proportionArray.push(
      ...Array.from({ length: selectedPrograms.length }, () => 1)
    );
  } else {
    proportionArray.push(
      ...selectedPrograms.map((alloc) => Number(alloc.byFte))
    );
  }
  return proportionArray;
};

const totalFteIsZeroGuard = (proportionArray) => {
  let fteTotalValue = proportionArray.reduce(
    (sum, val) => sum.add(getMoneyObj(val)),
    getMoneyObj(0)
  );
  if (fteTotalValue.amount === 0) {
    return true;
  }
  return false;
};

const calculateNotSelectedProgramDollars = (
  _selectedItemAllocations,
  selectedPrograms
) => {
  return _selectedItemAllocations
    .filter(
      (alloc) =>
        !selectedPrograms
          .map((prog) => prog.programId)
          .includes(alloc.programId)
    )
    .reduce((acc, curr) => acc.add(getMoneyObj(curr.dollars)), getMoneyObj(0));
};

const allocateAvailableDollars = (
  notSelectedProgramDollars,
  combinedTotalCostOfItems,
  proportionArray
) => {
  return combinedTotalCostOfItems
    .subtract(notSelectedProgramDollars)
    .allocate(proportionArray);
};

const assignSelectedProgramsAndSelectedItemAllocations = (
  selectedPrograms,
  dollarsArray,
  _selectedItemAllocations
) => {
  selectedPrograms.forEach((program, i) => {
    program.dollars = dollarsArray[i].amount;
  });

  _selectedItemAllocations.forEach((allocation) => {
    let matchedProgram = selectedPrograms.find(
      (prog) => prog.uniqueId === allocation.uniqueId
    );
    if (matchedProgram) {
      allocation.dollars = matchedProgram.dollars / 100;
    }
  });
};

const normalizeData = (data, normalizationMargin, items) => {
  let totalCost = items.reduce((acc, curr) => acc + curr.total_cost, 0);
  let medians = data.map((d) => d.median);

  data.forEach((d, i) => {
    if (i < data.length - 1) {
      d.nMedian = Number(
        (
          (d.median / medians.reduce((a, b) => a + b, 0)) *
          normalizationMargin
        ).toFixed(2)
      );
    } else {
      const clonedData = JSON.parse(JSON.stringify(data)).slice(
        0,
        data.length - 1
      );
      d.nMedian =
        normalizationMargin -
        clonedData.reduce((sum, acc) => sum + acc.nMedian, 0);
    }
    d.nMin = Number(((d.nMedian * d.min) / d.median).toFixed(2));
    d.nMax = Number(((d.nMedian * d.max) / d.median).toFixed(2));
    d.nQ1 = Number(((d.nMedian * d.q1) / d.median).toFixed(2));
    d.nQ3 = Number(((d.nMedian * d.q3) / d.median).toFixed(2));
    d.programCost = Number(((d.nMedian * totalCost) / 100).toFixed(2)); //this needs to be converted to money object

  });
  return data;
};

const calculateMedian = (arr) => {
  arr.sort((a, b) => a - b);
  const middleIndex = Math.floor(arr.length / 2);

  if (arr.length % 2 === 0) {
    return (arr[middleIndex - 1] + arr[middleIndex]) / 2;
  } else {
    return Number(arr[middleIndex].toFixed(2));
  }
};

const calculateQ1 = (arr) => {
  arr.sort((a, b) => a - b);
  const middleIndex = Math.floor(arr.length / 2);

  return calculateMedian(arr.slice(0, middleIndex));
};

const calculateQ3 = (arr) => {
  arr.sort((a, b) => a - b);
  const middleIndex = Math.floor(arr.length / 2);

  return calculateMedian(arr.slice(middleIndex));
};

const generateRequestObjectProgramInfo = (gridApiRef, selectedItems) => {
  let rows = [];
  const programInfo = [];

  const selectedPrograms = gridApiRef.current.api.getSelectedRows();
  gridApiRef.current.api.forEachNode((node) => rows.push(node.data));
  selectedItems.forEach((item) => {
    selectedPrograms.forEach((program) => {
      programInfo.push({
        programName: program.programName,
        programDescription: program.description,
        programId: program.programId,
        string: `${item.object_categories_json
          .filter((cat) => cat.number === 1)
          .map((x) => x.name)}---${item.object_categories_json
          .filter((cat) => cat.number === 2)
          .map((x) => x.name)}---${program.programName}---${program.description}---${program.costCenter.name
        }`,
      });
    });
  });

  return { programInfo, selectedPrograms };
};

const calculatePercentileData = (recommendationData, selectedItemsLength) => {
  let percentileData = recommendationData.map((d) => {
    let roundedPredictions = d.predictions.map((prediction) =>
      Number(prediction.toFixed(2))
    );
    return {
      programId: d.programId,
      //string: d.string,
      programName: d.programName,
      programDescription: d.programDescription,
      median: calculateMedian(roundedPredictions),
      min: Math.min(...roundedPredictions),
      max: Math.max(...roundedPredictions),
      q1: calculateQ1(roundedPredictions),
      q3: calculateQ3(roundedPredictions),
    };
  });
  let data = sumDuplicateProgramMedians(percentileData, selectedItemsLength);
  return data;
};

const sumDuplicateProgramMedians = (arr, selectedItemsLength) => {
  let resultArray = [];
  arr.sort((a, b) => a.programId - b.programId);

  for (let i = 0; i < arr.length; i += selectedItemsLength) {
    let reworkedChunk = [];
    const chunk = arr.slice(i, i + selectedItemsLength);
    reworkedChunk.push({
      programId: chunk[0].programId,
      programName: chunk[0].programName,
      programDescription: chunk[0].programDescription,
      median: Number(
        (
          chunk.reduce((acc, curr) => acc + curr.median, 0) /
          selectedItemsLength
        ).toFixed(2)
      ),
      min: Number(
        (
          chunk.reduce((acc, curr) => acc + curr.min, 0) / selectedItemsLength
        ).toFixed(2)
      ),
      max: Number(
        (
          chunk.reduce((acc, curr) => acc + curr.max, 0) / selectedItemsLength
        ).toFixed(2)
      ),
      q1: Number(
        (
          chunk.reduce((acc, curr) => acc + curr.q1, 0) / selectedItemsLength
        ).toFixed(2)
      ),
      q3: Number(
        (
          chunk.reduce((acc, curr) => acc + curr.q3, 0) / selectedItemsLength
        ).toFixed(2)
      ),
    });
    resultArray.push(...reworkedChunk);
  }

  return resultArray;
};

export const getRawMargin = (notSelectedPrograms) => {
  const scale = 100;

  let marginObj = getMoneyObj(100 * scale).subtract(
    notSelectedPrograms.reduce(
      (acc, program) => {
        return acc.add(getMoneyObj(Number(program.percent) * scale));
      },
      getMoneyObj(0)
    )
  );

  return marginObj.amount / (100 * scale);
};

export const updateSelectedItemAllocations = (allocations, normalizedData) => {
  return allocations.map((allocation) => {
    const recommendedProgram = normalizedData.find(
      (program) => program.programId === allocation.programId
    );
    return {
      ...allocation,
      percent: recommendedProgram ? recommendedProgram.nMedian : allocation.percent,
      dollars: 0
    };
  });
};

export const syncItemAllocations = (selectedItems, updatedAllocations) => {
  selectedItems.forEach(item => {
    const totalItemCost = getMoneyObj(item.total_cost);
    const maxPercents = getMoneyObj(100);
    let allocatedSum = getMoneyObj(0);

    item.allocations.forEach(allocation => {
      const editedAllocation = updatedAllocations.find(ua => ua.programId === allocation.programId);
      if (editedAllocation !== undefined) {
        allocation.percent = editedAllocation.percent;
      }
      allocatedSum = allocatedSum.add(getMoneyObj(allocation.percent));
    });

    const newAllocations = updatedAllocations.filter(editedAllocation => !(item.allocations.map(a => a.programId)).includes(editedAllocation.programId) && editedAllocation.percent > 0);
    newAllocations.forEach(newAllocation => {
      item.allocations.push({...newAllocation, id: null});
      allocatedSum = allocatedSum.add(getMoneyObj(newAllocation.percent));
    });

    const missing = maxPercents.subtract(allocatedSum);
    const proportions = [...item.allocations.map(a => a.percent), missing.amount / 100];
    const distributedAmounts = totalItemCost.allocate(proportions);
    item.allocations.forEach((allocation, index) => {
      const dollarValue = distributedAmounts[index].amount / 100;
      allocation.dollars = dollarValue;
      const targetUpdated = updatedAllocations.find(ua => ua.programId === allocation.programId);
      if (targetUpdated) {
        const currentSum = getMoneyObj(targetUpdated.dollars);
        const newSum = currentSum.add(getMoneyObj(dollarValue));
        targetUpdated.dollars = newSum.amount / 100;
      }
    });
  });
};

const getNotSelectedPrograms = (gridApiRef) => {
  let notSelectedPrograms = [];
  gridApiRef.current.api.forEachNode((node) => {
    if (!node.isSelected()) {
      notSelectedPrograms.push(node.data);
    }
  });
  return notSelectedPrograms;
};

const getPlotStyles = (theme) => {
  return {
    min: { stroke: theme === LIGHT ? LIGHT_THEME_COLOR : DARK_THEME_COLOR },
    max: { stroke: theme === LIGHT ? LIGHT_THEME_COLOR : DARK_THEME_COLOR },
    q1: { fill: QUARTILE_COLOR },
    q3: { fill: QUARTILE_COLOR },
    median: {
      stroke: theme === LIGHT ? LIGHT_THEME_COLOR : DARK_THEME_COLOR,
      strokeWidth: 2,
    },
    whisker: { stroke: theme === LIGHT ? LIGHT_THEME_COLOR : DARK_THEME_COLOR },
    minLabels: {
      fill: theme === LIGHT ? LIGHT_THEME_COLOR : DARK_THEME_COLOR,
      fontFamily: 'sans-serif',
      fontSize: '12px',
      fontWeight: 'bold',
    },
    maxLabels: {
      fill: theme === LIGHT ? LIGHT_THEME_COLOR : DARK_THEME_COLOR,
      fontFamily: 'sans-serif',
      fontSize: '12px',
      fontWeight: 'bold',
    },
    q1Labels: {
      fill: theme === LIGHT ? LIGHT_THEME_COLOR : DARK_THEME_COLOR,
      fontFamily: 'sans-serif',
      fontSize: '12px',
      fontWeight: 'bold',
    },
    q3Labels: {
      fill: theme === LIGHT ? LIGHT_THEME_COLOR : DARK_THEME_COLOR,
      fontFamily: 'sans-serif',
      fontSize: '12px',
      fontWeight: 'bold',
    },
    medianLabels: {
      fill: theme === LIGHT ? LIGHT_THEME_COLOR : DARK_THEME_COLOR,
      fontFamily: 'sans-serif',
      fontSize: '12px',
      fontWeight: 'bold',
    },
  };
};

const allocationTableColumnDefs = (costType, allocationMode, nameConventions, showColumns) => {
  let showColumnsCostAllocation = showColumns?.costAllocationColumns;

  let columns = [
    {
      colId: '0',
      headerName: 'Allocation',
      headerTooltip: 'Allocation',
      context: 'Allocation',
      field:
        allocationMode === COST_ALLOCATION_TOGGLE_BUTTON_LABELS[0]
          ? 'percent'
          : 'dollars',
      tooltipValueGetter: (params) =>
        allocationMode === COST_ALLOCATION_TOGGLE_BUTTON_LABELS[0]
          ? `${Number(params.value).toFixed(2)}%`
          : totalCostFormat(params.value),
      editable: true,
      valueSetter: (params) => {
        if (params.newValue === null) {
          return false;
        }

        const sanitizedValue = params.newValue.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
        if (!sanitizedValue) {
          return false;
        }

        params.data[
          allocationMode === COST_ALLOCATION_TOGGLE_BUTTON_LABELS[0]
            ? 'percent'
            : 'dollars'
        ] = Number(sanitizedValue);
        return true;
      },
      valueFormatter: (params) =>
        allocationMode === COST_ALLOCATION_TOGGLE_BUTTON_LABELS[0]
          ? `${Number(params.value).toFixed(2)}%`
          : totalCostFormat(params.value),
      cellEditor: 'agNumberCellEditor',
      //sort: 'desc',
      hide: !showColumnsCostAllocation?.find((column) => column.name === 'Allocation')?.show,
      width: TABLE_COLUMN_WIDTH.LG,
    },
    {
      colId: '1',
      headerName: nameConventions ? nameConventions[PROGRAM].alias : PROGRAM,
      headerTooltip: nameConventions ? nameConventions[PROGRAM].alias : PROGRAM,
      context: PROGRAM,
      field: 'programName',
      tooltipField: 'programName',
      suppressColumnsToolPanel: true,
    },
    {
      colId: '2',
      headerName: nameConventions
        ? nameConventions[PROGRAM_DEPARTMENT].alias
        : PROGRAM_DEPARTMENT,
      headerTooltip: nameConventions
        ? nameConventions[PROGRAM_DEPARTMENT].alias
        : PROGRAM_DEPARTMENT,
      context: PROGRAM_DEPARTMENT,
      field: 'department',
      tooltipField: 'department',
      hide: !showColumnsCostAllocation?.find((column) => column.name === PROGRAM_DEPARTMENT)?.show,
      width: TABLE_COLUMN_WIDTH.DEFAULT
    },
    {
      colId: '3',
      headerName: nameConventions
        ? nameConventions[PROGRAM_DIVISION].alias
        : PROGRAM_DIVISION,
      headerTooltip: nameConventions
        ? nameConventions[PROGRAM_DIVISION].alias
        : PROGRAM_DIVISION,
      context: PROGRAM_DIVISION,
      field: 'division',
      tooltipField: 'division',
      hide: !showColumnsCostAllocation?.find((column) => column.name === PROGRAM_DIVISION)?.show,
    },
    {
      colId: '4',
      headerName: 'Service Type',
      headerTooltip: 'Service Type',
      context: 'Service Type',
      field: 'serviceType.name',
      tooltipField: 'serviceType.name',
      valueGetter: (params) => params.data.serviceType.name,
      hide: !showColumnsCostAllocation?.find((column) => column.name === 'Service Type')?.show,
      width: TABLE_COLUMN_WIDTH.MD,
    },
    {
      colId: '5',
      headerName: nameConventions
        ? nameConventions[USER_GROUP].alias
        : USER_GROUP,
      headerTooltip: nameConventions
        ? nameConventions[USER_GROUP].alias
        : USER_GROUP,
      context: USER_GROUP,
      field: 'costCenter.name',
      tooltipField: 'costCenter.name',
      hide: !showColumnsCostAllocation?.find((column) => column.name === USER_GROUP)?.show,
      width: TABLE_COLUMN_WIDTH.DEFAULT
    },
    {
      colId: '6',
      headerName:
        'Program number',
      headerTooltip:
        'Program number',
      context: 'Program number',
      field: 'programNumber',
      tooltipField: 'programNumber',
      hide: !showColumnsCostAllocation?.find((column) => column.name === 'ProgramNum')?.show,
      width: TABLE_COLUMN_WIDTH.MD,
    },
  ];

  if (costType !== OBJECT_TYPE_DICTIONARY[1])
    columns.push({
      colId: '7',
      headerName: 'FTE',
      context: 'FTE',
      field: 'byFte',
      valueFormatter: (params) => Number(params.value).toFixed(2),
      comparator: AGGridColumnNumberComparator,
      hide: !showColumnsCostAllocation?.find((column) => column.name === 'FTE')?.show,
      width: TABLE_COLUMN_WIDTH.XS
    });

  columns.push({
    colId: '8',
    headerName: 'ProgID',
    headerTooltip:
      (nameConventions ? nameConventions[PROGRAM].alias : PROGRAM) + 'ID',
    context: 'ProgID',
    field: 'programId',
    tooltipField: 'programId',
    hide: !showColumnsCostAllocation?.find((column) => column.name === 'ProgID')?.show,
    width: TABLE_COLUMN_WIDTH.XS
  });

  return columns;
};

const createAllocationObject = (item, allocation) => {
  return {
    id: allocation.id,
    itemId: item.itemid,
    programId: allocation.programId,
    programName: allocation.programName,
    programCost: getMoneyObj(allocation.dollars),
    percentsApplied: getMoneyObj(allocation.percent),
    durationTypeId: allocation.durationTypeId
      ? allocation.durationTypeId
      : item.durationTypeId,
    objectTypeId:
      item.object_type === OBJECT_TYPE_DICTIONARY[1]
        ? PERSONNEL_OBJECT_TYPE_ID
        : NONPERSONNEL_OBJECT_TYPE_ID,
    costCenterId: allocation.costCenter.id,
    itemTotalCost: item.total_cost,
    byFte:
      item.object_type === OBJECT_TYPE_DICTIONARY[1]
        ? Number(((allocation.percent / 100) * item.number_of_items).toFixed(2))
        : 0,
  };
};

const createAllocationsArray = (
  selectedItems,
) => {
  const allocationsArray = [];
  selectedItems?.forEach((item) => {
    item?.allocations?.forEach((allocation) => {
      if (allocation.id || allocation.dollars) {
        allocationsArray.push(createAllocationObject(item, allocation));
      }
    });
  });

  return { allocationsArray };
};

const calculateDollarsArray = (costingRecommendation, selectedItems) => {
  const result = [];
  const medians = costingRecommendation.map((program) => program.nMedian);
  const selectedItemsTotalCostMoney = selectedItems.reduce(
    (accumulator, item) => {
      return accumulator.add(getMoneyObj(item.total_cost));
    },
    getMoneyObj(0)
  );
  const totalAllocationsMoney = medians.reduce((accumulator, median) => {
    return accumulator.add(getMoneyObj(median));
  }, getMoneyObj(0));
  const unallocated = getMoneyObj(100).subtract(totalAllocationsMoney);
  const proportionsArray =
    unallocated.amount > 0 ? [...medians, unallocated.amount / 100] : medians;
  const allocatedDollars =
    selectedItemsTotalCostMoney.allocate(proportionsArray);
  costingRecommendation.forEach((program, i) => {
    result.push({
      programId: program.programId,
      dollars: allocatedDollars[i],
    });
  });
  return result;
};

const getPredictionInfoTableColumns = (nameConventions) => {
  return [
    {
      headerName: nameConventions ? nameConventions[PROGRAM].alias : PROGRAM,
      headerTooltip: nameConventions ? nameConventions[PROGRAM].alias : PROGRAM,
      field: 'programName',
      tooltipField: 'Program',
      context: 'Program',
    },
    {
      headerName: nameConventions ? nameConventions[PROGRAM].alias + ' ' + DESCRIPTION : PROGRAM + ' ' + DESCRIPTION,
      headerTooltip: nameConventions ? nameConventions[PROGRAM].alias + ' ' + DESCRIPTION : PROGRAM + ' ' + DESCRIPTION,
      field: 'programDescription',
      tooltipField: 'Program description',
      context: 'Program description',
    },
    {
      headerName: 'Median',
      headerTooltip: 'Median',
      field: 'nMedian',
      tooltipField: 'Median',
      context: 'Median',
      valueFormatter: (params) => Number(params.value).toFixed(2),
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
    },
    {
      headerName: 'Minimum',
      headerTooltip: 'Minimum',
      field: 'nMin',
      tooltipField: 'Minimum',
      context: 'Minimum',
      valueFormatter: (params) => Number(params.value).toFixed(2),
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
    },
    {
      headerName: 'Maximum',
      headerTooltip: 'Maximum',
      field: 'nMax',
      tooltipField: 'Maximum',
      context: 'Maximum',
      valueFormatter: (params) => Number(params.value).toFixed(2),
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
    },
  ];
};

const getSelectionToolBarButtons = (
  clearAllocations,
  handleAllocateFTE,
  handleAllocateEvenly,
  recommendAllocations,
  selectedRows,
  currentTabValue,
  allocationMode,
  clearShowCondition
) => {
  const Buttons = [
    {
      id: 'evenly',
      type: 'selection',
      name: 'Evenly',
      icon: 'format_align_center',
      tab: 'both',
      showCondition: true,
      onClickFunction: () => handleAllocateEvenly(selectedRows),
    },
    {
      id: 'clear',
      type: 'selection',
      name: 'Clear',
      icon: 'eraser',
      tab: 'both',
      showCondition: clearShowCondition,
      onClickFunction: () => clearAllocations(selectedRows),
    },
    {
      id: 'fte',
      type: 'selection',
      name: 'Allocate by FTE',
      icon: 'person',
      tab: 'NonPersonnel',
      showCondition: true,
      onClickFunction: () => handleAllocateFTE(selectedRows),
    },
    {
      name: 'Recommend',
      onClickFunction: () => recommendAllocations(selectedRows),
      id: 'recommend',
      useAiButton: true,
      type: 'selection',
      tab: 'both',
      showCondition: currentTabValue === PERSONNEL ? true : allocationMode === '%' ? true : false,
    },
  ];
  return Buttons;
};

const calculateAdditionalTableOffset = (
  currentTabValue,
  selectedAllocations
) => {
  return selectedAllocations.length > 0
    ? currentTabValue === PERSONNEL
      ? 56
      : 91
    : currentTabValue === PERSONNEL
      ? 0
      : 35;
};

export {
  LIGHT,
  BUTTONS,
  normalizeData,
  generateRequestObjectProgramInfo,
  calculatePercentileData,
  getNotSelectedPrograms,
  getPlotStyles,
  allocateProportionally,
  allocationTableColumnDefs,
  createAllocationsArray,
  calculateDollarsArray,
  getPredictionInfoTableColumns,
  getSelectionToolBarButtons,
  calculateAdditionalTableOffset,
  totalFteIsZeroGuard
};
